import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from '../hooks/useInView';
import { useSeo } from '../hooks/useSeo';
import { COMMERCE_API_BASE_URL, LANDING_PAGE_PRODUCT_ID } from '../config';

function formatPrice(product, language) {
  const locale = language === 'pt' ? 'pt-BR' : 'en-US';
  return new Intl.NumberFormat(locale, { style: 'currency', currency: product.currency }).format(product.priceAmount);
}

export default function WebsitesPage() {
  const [ref, inView] = useInView();
  const { t, i18n } = useTranslation();
  useSeo({
    title: t('seo.websites.title'),
    description: t('seo.websites.description'),
    path: '/projects/websites',
  });
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | error
  const [product, setProduct] = useState(null);
  const [productStatus, setProductStatus] = useState('loading'); // loading | ready | error

  useEffect(() => {
    let cancelled = false;

    fetch(`${COMMERCE_API_BASE_URL}/products/${LANDING_PAGE_PRODUCT_ID}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Product request failed');
        }
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        setProduct(data);
        setProductStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setProductStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const response = await fetch(`${COMMERCE_API_BASE_URL}/orders/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: crypto.randomUUID(),
          items: [{ productId: LANDING_PAGE_PRODUCT_ID, quantity: 1 }],
          customerEmail: email,
        }),
      });

      if (!response.ok) {
        throw new Error('Checkout request failed');
      }

      const order = await response.json();
      if (!order.checkoutUrl) {
        throw new Error('No checkout URL returned');
      }

      window.location.href = order.checkoutUrl;
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <section id="websites" className={`section websites${inView ? ' in-view' : ''}`} ref={ref}>
      <div className="section__container websites__inner">
        <h2 className="section__title">{t('websites.title')}</h2>
        <p className="websites__text">{t('websites.text')}</p>

        <div className="websites__card">
          {productStatus === 'ready' && (
            <span className="websites__price">{formatPrice(product, i18n.language)}</span>
          )}
          <p className="websites__card-text">{t('websites.cardText')}</p>

          {productStatus === 'error' ? (
            <p className="websites__error">{t('websites.productLoadError')}</p>
          ) : (
            <form className="websites__form" onSubmit={handleSubmit}>
              <label htmlFor="websites-email" className="websites__label">
                {t('websites.emailLabel')}
              </label>
              <input
                id="websites-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('websites.emailPlaceholder')}
                className="websites__input"
              />
              <button
                type="submit"
                className="btn btn--primary"
                disabled={status === 'loading' || productStatus === 'loading'}
              >
                {status === 'loading' ? t('websites.submitLoading') : t('websites.submitIdle')}
              </button>
            </form>
          )}

          {status === 'error' && <p className="websites__error">{t('websites.errorText')}</p>}
        </div>
      </div>
    </section>
  );
}
